import React, { useState, useEffect } from 'react';
import { Box, Typography, Slider, Paper, Tooltip, IconButton, Button, Alert, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faSave, faUndo } from '@fortawesome/free-solid-svg-icons';
import { getClientSettings, updateClientSettings } from '../../services/api';

interface RAGPreferencesSettingsProps {
  clientId: string;
}

interface SourceWeights {
  website: number;
  document: number;
  youtube: number;
}

const RAGPreferencesSettings: React.FC<RAGPreferencesSettingsProps> = ({ clientId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [sourceWeights, setSourceWeights] = useState<SourceWeights>({
    website: 1.15,
    document: 1.00,
    youtube: 0.85
  });
  const [originalWeights, setOriginalWeights] = useState<SourceWeights>({
    website: 1.15,
    document: 1.00,
    youtube: 0.85
  });
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [clientId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await getClientSettings(clientId);
      
      if (settings.rag_preferences) {
        setEnabled(settings.rag_preferences.enabled ?? true);
        if (settings.rag_preferences.source_weights) {
          const weights = settings.rag_preferences.source_weights;
          setSourceWeights(weights);
          setOriginalWeights(weights);
        }
      }
    } catch (error) {
      console.error('Error loading RAG preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWeightChange = (source: keyof SourceWeights, value: number) => {
    setSourceWeights(prev => ({
      ...prev,
      [source]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateClientSettings(clientId, {
        rag_preferences: {
          enabled,
          source_weights: sourceWeights
        }
      });
      setOriginalWeights(sourceWeights);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving RAG preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSourceWeights(originalWeights);
  };

  const hasChanges = JSON.stringify(sourceWeights) !== JSON.stringify(originalWeights);

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-64">
        <CircularProgress />
      </Box>
    );
  }

  const getWeightLabel = (value: number): string => {
    if (value === 1.0) return 'Baseline';
    if (value > 1.0) return `+${Math.round((value - 1) * 100)}% Priority`;
    return `${Math.round((1 - value) * 100)}% Reduced`;
  };

  const getWeightColor = (value: number): string => {
    if (value === 1.0) return '#666';
    if (value > 1.0) return '#2e7d32';
    return '#d32f2f';
  };

  return (
    <Box>
      <Box className="mb-6">
        <Typography variant="h5" className="font-semibold mb-2 flex items-center">
          Source Preferences
          <Tooltip title="Adjust how different content sources are prioritized when searching your knowledge base">
            <IconButton size="small" className="ml-2">
              <FontAwesomeIcon icon={faInfoCircle} className="text-gray-500 text-sm" />
            </IconButton>
          </Tooltip>
        </Typography>
        <Typography variant="body2" className="text-gray-600">
          Fine-tune how your AI prioritizes different content sources when answering questions
        </Typography>
      </Box>

      {showSuccess && (
        <Alert severity="success" className="mb-4">
          Source preferences saved successfully!
        </Alert>
      )}

      <Paper className="p-6 mb-4">
        <Box className="mb-6">
          <Typography variant="body1" className="mb-4 text-gray-700">
            When multiple sources contain similar information, these weights influence which content is preferred.
            Higher values give priority to that source type.
          </Typography>
        </Box>

        {/* Website Content */}
        <Box className="mb-6">
          <Box className="flex justify-between items-center mb-2">
            <Box>
              <Typography variant="subtitle1" className="font-medium">
                🌐 Website Content
              </Typography>
              <Typography variant="caption" className="text-gray-600">
                Pages scraped from your website
              </Typography>
            </Box>
            <Typography 
              variant="body2" 
              className="font-medium"
              style={{ color: getWeightColor(sourceWeights.website) }}
            >
              {getWeightLabel(sourceWeights.website)}
            </Typography>
          </Box>
          <Slider
            value={sourceWeights.website}
            onChange={(_, value) => handleWeightChange('website', value as number)}
            min={0.5}
            max={1.5}
            step={0.05}
            marks={[
              { value: 0.5, label: '-50%' },
              { value: 1.0, label: 'Baseline' },
              { value: 1.5, label: '+50%' }
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => value.toFixed(2)}
          />
        </Box>

        {/* Document Content */}
        <Box className="mb-6">
          <Box className="flex justify-between items-center mb-2">
            <Box>
              <Typography variant="subtitle1" className="font-medium">
                📄 Documents
              </Typography>
              <Typography variant="caption" className="text-gray-600">
                PDFs, Word docs, and other uploaded files
              </Typography>
            </Box>
            <Typography 
              variant="body2" 
              className="font-medium"
              style={{ color: getWeightColor(sourceWeights.document) }}
            >
              {getWeightLabel(sourceWeights.document)}
            </Typography>
          </Box>
          <Slider
            value={sourceWeights.document}
            onChange={(_, value) => handleWeightChange('document', value as number)}
            min={0.5}
            max={1.5}
            step={0.05}
            marks={[
              { value: 0.5, label: '-50%' },
              { value: 1.0, label: 'Baseline' },
              { value: 1.5, label: '+50%' }
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => value.toFixed(2)}
          />
        </Box>

        {/* YouTube Content */}
        <Box className="mb-6">
          <Box className="flex justify-between items-center mb-2">
            <Box>
              <Typography variant="subtitle1" className="font-medium">
                📹 YouTube Videos
              </Typography>
              <Typography variant="caption" className="text-gray-600">
                Transcripts from YouTube videos
              </Typography>
            </Box>
            <Typography 
              variant="body2" 
              className="font-medium"
              style={{ color: getWeightColor(sourceWeights.youtube) }}
            >
              {getWeightLabel(sourceWeights.youtube)}
            </Typography>
          </Box>
          <Slider
            value={sourceWeights.youtube}
            onChange={(_, value) => handleWeightChange('youtube', value as number)}
            min={0.5}
            max={1.5}
            step={0.05}
            marks={[
              { value: 0.5, label: '-50%' },
              { value: 1.0, label: 'Baseline' },
              { value: 1.5, label: '+50%' }
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => value.toFixed(2)}
          />
        </Box>

        <Box className="mt-6 p-4 bg-blue-50 rounded-lg">
          <Typography variant="caption" className="text-blue-800">
            <strong>How it works:</strong> When searching for information, content with higher weights will be preferred 
            when multiple sources have similar relevance. Very high-quality matches always take priority regardless of weights.
          </Typography>
        </Box>
      </Paper>

      <Box className="flex justify-end gap-2">
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={!hasChanges || saving}
          startIcon={<FontAwesomeIcon icon={faUndo} />}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          startIcon={<FontAwesomeIcon icon={faSave} />}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default RAGPreferencesSettings;